import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isSplitDefined } from '@/api/getSplitDetails';
import { Price } from '@/components/Price/Price';
import SummarySkeleton from '@/components/Skeletons/SummarySkeleton';
import SummaryRow from '@/components/Summary/SummaryRow';
import SummaryTotal from '@/components/Summary/SummaryTotal';
import { useCheckoutContext } from '@/context/CheckoutContext/CheckoutContext';
import { useSplitDetails } from '@/hooks/useSplitDetails';
import { calculateFinalTipAmount } from '@/utils/tipCalculations';
import styles from '../Summary.module.scss';

interface Props {
  serviceFee?: number;
  orderNumber: string;
  onToggleServiceFeeInfo?: () => void;
  tipPercentage: number;
  customTipAmount?: number | null;
}

export const SplitSummary = ({
  orderNumber,
  serviceFee,
  onToggleServiceFeeInfo,
  tipPercentage,
  customTipAmount,
}: Props) => {
  const { state } = useCheckoutContext();
  const { t } = useTranslation();
  const { data: splitDetails, isLoading: isSplitDetailsLoading } = useSplitDetails({
    orderNumber,
    enablePolling: true,
    pollingInterval: 30,
  });

  const summary = useMemo(() => {
    if (isSplitDefined(splitDetails)) {
      // The onePersonSplit fallback is an equal-split concept — for an items split
      // it is null, and the share is always the confirmed selection sum (0 until
      // the payer confirms a selection)
      const shareBase =
        splitDetails.type === 'SPLIT_BY_ITEMS'
          ? state.yourShare
          : state.yourShare || splitDetails.onePersonSplit;
      const yourTip = calculateFinalTipAmount(customTipAmount, shareBase, tipPercentage);

      // In case we are going to pay for two person from 3 for example
      const yourTotalForAdjustedSplit = state.yourShare + yourTip + (serviceFee || 0);
      const yourTotalForOriginalSplit = shareBase + yourTip + (serviceFee || 0);

      return {
        totalBillAmount: splitDetails.totalAmount,
        remainingAmount: splitDetails.remainingAmount,
        yourTip,
        yourTotal: state.numberOfSplits > 0 ? yourTotalForAdjustedSplit : yourTotalForOriginalSplit,
      };
    }
    return undefined;
  }, [
    splitDetails,
    serviceFee,
    tipPercentage,
    customTipAmount,
    state.yourShare,
    state.numberOfSplits,
  ]);

  if (isSplitDetailsLoading || !summary) {
    return <SummarySkeleton />;
  }

  return (
    <div className={styles.root}>
      <h2>{t('checkout.splitSummary.splitSummary')}</h2>

      <SummaryRow
        label={t('checkout.splitSummary.totalBill')}
        value={<Price amount={summary.totalBillAmount} />}
        testId="total-bill"
      />

      <SummaryRow
        label={t('checkout.splitEqualMode.leftToPay')}
        value={<Price amount={summary.remainingAmount} />}
        testId="left-to-pay"
      />

      <SummaryRow
        label={t('checkout.summary.tips')}
        value={<Price amount={summary.yourTip} />}
        testId="summary-tips"
      />

      {serviceFee !== undefined && serviceFee !== 0 && (
        <SummaryRow
          label={t('checkout.summary.serviceFee')}
          value={<Price amount={serviceFee} />}
          testId="service-fee"
          onClick={onToggleServiceFeeInfo}
        />
      )}

      <SummaryTotal
        label={t('checkout.splitSummary.yourShare')}
        value={<Price amount={summary.yourTotal} />}
        testId="your-share"
      />
    </div>
  );
};
